import { Alert, Button, EmptyState, Skeleton, Table, TableBody, TableCell, TableHeader, TableRow, cn, formatDate } from '@catering/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import type { JSX } from 'react';

import { formatPriceCents } from '../../contracts/format-price';
import { getAgingReport, type AgingBucket } from '../../customer-account/api';
import { AGING_BUCKET_LABELS } from '../../customer-account/labels';

export interface CustomerAgingPageProps {
  customerId: string;
}

/**
 * `customer-account-statement/spec.md` → "Vista de antigüedad resalta
 * deuda crítica": the `90+` row gets a danger tone, not a new severity
 * component — `TableRow`'s own `className` is enough. Each bucket row is a
 * native `<details>`/`<summary>` disclosure for its invoice detail: no new
 * expand/collapse component, same "sin componentes nuevos" constraint
 * `tasks.md` PR 13's REFACTOR step sets.
 */
export function CustomerAgingPage({ customerId }: CustomerAgingPageProps): JSX.Element {
  const query = useQuery({
    queryKey: ['customer-aging-report', customerId],
    queryFn: () => getAgingReport(customerId),
  });

  const totalCents = (query.data ?? []).reduce((sum, bucket) => sum + bucket.totalCents, 0);
  const hasDebt = totalCents > 0;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-semibold text-slate-900">Antigüedad de deuda</h2>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar la antigüedad de deuda">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al cargar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && !hasDebt ? (
        <EmptyState title="Esta empresa no tiene deuda pendiente." />
      ) : null}

      {query.data !== undefined && hasDebt ? (
        <Table>
          <caption className="sr-only">Antigüedad de deuda por tramo</caption>
          <thead>
            <TableRow>
              <TableHeader>Tramo</TableHeader>
              <TableHeader>Facturas</TableHeader>
              <TableHeader>Monto</TableHeader>
            </TableRow>
          </thead>
          <TableBody>
            {query.data.map((bucket) => (
              <BucketRow key={bucket.bucket} bucket={bucket} />
            ))}
            <TableRow className="font-semibold text-slate-900">
              <TableCell>Total</TableCell>
              <TableCell>{query.data.reduce((sum, bucket) => sum + bucket.invoiceCount, 0)}</TableCell>
              <TableCell>{formatPriceCents(totalCents)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}

function BucketRow({ bucket }: { bucket: AgingBucket }): JSX.Element {
  const isCritical = bucket.bucket === '90+';

  if (bucket.invoiceCount === 0) {
    return (
      <TableRow className={cn(isCritical && 'bg-red-50')}>
        <TableCell>
          <BucketLabel bucket={bucket} isCritical={isCritical} />
        </TableCell>
        <TableCell>0</TableCell>
        <TableCell>{formatPriceCents(0)}</TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <TableRow className={cn(isCritical && 'bg-red-50')}>
        <TableCell colSpan={3} className="p-0">
          <details>
            <summary className="grid cursor-pointer grid-cols-3 gap-2 px-3 py-2 text-sm">
              <BucketLabel bucket={bucket} isCritical={isCritical} />
              <span>{bucket.invoiceCount}</span>
              <span>{formatPriceCents(bucket.totalCents)}</span>
            </summary>
            <div className="border-t border-slate-200 px-3 py-2">
              <Table>
                <caption className="sr-only">Facturas del tramo {AGING_BUCKET_LABELS[bucket.bucket]}</caption>
                <thead>
                  <TableRow>
                    <TableHeader>Factura</TableHeader>
                    <TableHeader>Vencimiento</TableHeader>
                    <TableHeader>Saldo</TableHeader>
                  </TableRow>
                </thead>
                <TableBody>
                  {bucket.invoices.map((invoice) => (
                    <TableRow key={invoice.invoiceId}>
                      <TableCell>
                        <Link
                          to="/invoices/$id"
                          params={{ id: invoice.invoiceId }}
                          className="font-medium text-slate-900 hover:underline"
                        >
                          {invoice.invoiceNumber}
                        </Link>
                      </TableCell>
                      <TableCell>{invoice.dueDate === null ? '—' : formatDate(invoice.dueDate)}</TableCell>
                      <TableCell>{formatPriceCents(invoice.balanceCents)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </details>
        </TableCell>
      </TableRow>
    </>
  );
}

function BucketLabel({ bucket, isCritical }: { bucket: AgingBucket; isCritical: boolean }): JSX.Element {
  return (
    <span className={cn('flex items-center gap-2', isCritical && 'font-semibold text-red-800')}>
      {isCritical ? <AlertTriangle aria-hidden="true" className="size-4 text-red-600" /> : null}
      {AGING_BUCKET_LABELS[bucket.bucket]}
    </span>
  );
}
